"use client";

import { useMemo, useRef, useState } from "react";
import { cn, extractTime, parseIranPlate, toJalali } from "@/lib";
import CarLicenseImg from "public/ir-flag.webp";
import irImg from "public/ir.webp";
import Image from "next/image";
import { BrAltArrowDown, BrAltArrowUp } from "solar-icon-react/br";
import {
  plateAlphabetMap,
  VehicleTypeKey,
  vehicleTypes,
} from "@/data/vehicleTypes";
import { IRawPlateInquiryMutationResDataBody } from "@/interfaces/inquiries/vehicle";

interface IProps {
  plateData: IRawPlateInquiryMutationResDataBody;
  isOpen?: boolean;
  onToggle?: () => void;
  plateInput: { national_code: string };
}

// Function to determine vehicle type based on plate letter
const getVehicleType = (letter: string): VehicleTypeKey => {
  // Check for exact matches in the map
  const vehicleType = plateAlphabetMap[letter];
  if (vehicleType) {
    return vehicleType;
  }

  const mapKey = Object.keys(plateAlphabetMap).find(
    (key) => key.includes(letter) || letter.includes(key.charAt(0)),
  );

  if (mapKey) {
    return plateAlphabetMap[mapKey];
  }

  // Default to personal for unknown letters
  return "personal";
};

const DetailPlateInquiry = ({
  plateData,
  isOpen = false,
  onToggle,
  plateInput,
}: IProps) => {
  const [open, setOpen] = useState(false);

  // Use external state if provided, otherwise fall back to internal state
  const isExpanded = onToggle ? isOpen : open;
  const contentRef = useRef<HTMLDivElement>(null);
  const { convertedPlateNum, plateId, detachDate, plateNum } = plateData;
  const { two, letter, three, region } = parseIranPlate(
    convertedPlateNum ?? "",
  );
  const plateSerial = two;
  const plateLetters = letter;
  const plateNumbers = three;
  const plateRegion = region;
  const inactiveDate = `${toJalali(detachDate)} - ${extractTime(detachDate)}`;

  // Determine vehicle type and colors based on plate letter
  const vehicleType = getVehicleType(plateLetters);
  const vehicleTypeInfo = vehicleTypes[vehicleType];
  const plateBackgroundColor = vehicleTypeInfo.color;
  const plateTextColor = vehicleTypeInfo.textColor || "text-black";

  const contentMaxHeight = useMemo(() => {
    if (!contentRef.current) return 0;
    return isExpanded ? contentRef.current.scrollHeight : 0;
  }, [isExpanded]);

  const isInactive = !!detachDate;

  return (
    <div className="dir-ltr p-2 sm:p-4">
      <div
        role="button"
        aria-expanded={isExpanded}
        onClick={onToggle ? onToggle : () => setOpen((v) => !v)}
        className="flex cursor-pointer items-stretch transition-all duration-300 select-none"
      >
        {/* Status indicator - now part of the flow */}
        <div
          className={cn(
            "flex w-12 items-center justify-center rounded-l-lg border-4 border-r-0 border-black text-white",
            isInactive
              ? "bg-error-light text-error-dark"
              : "text-success-main bg-green-200",
          )}
        >
          <span className="-rotate-90 text-base font-semibold whitespace-nowrap md:text-2xl">
            {isInactive ? "فک‌شده" : "فعال"}
          </span>
        </div>

        {/* License Plate */}
        <div
          className={cn(
            "relative flex-1 overflow-hidden rounded-lg rounded-l-none border-4 border-black",
            plateBackgroundColor,
          )}
        >
          <div className="absolute -bottom-2 left-[50%] z-10 flex -translate-x-1/2 items-center justify-center rounded-xl bg-black px-3">
            {isExpanded ? (
              <BrAltArrowUp className="text-white" size={22} />
            ) : (
              <BrAltArrowDown className="text-white" size={22} />
            )}
          </div>
          <div className="flex h-20 items-stretch">
            {/* Left Blue Section with Flag and I.R. IRAN */}
            <div className="flex w-[30px] flex-col items-center justify-center bg-[#003499] text-white sm:w-auto">
              <Image
                src={CarLicenseImg}
                alt="شماره پلاک"
                className="h-full w-full object-contain"
              />
            </div>

            {/* Main Numbers Section */}
            <div className="dir-rtl relative flex flex-1 items-center justify-center">
              <div
                className={cn(
                  "flex items-center justify-center gap-2 px-4 font-bold md:gap-6 lg:gap-8",
                  plateTextColor,
                )}
              >
                <span className="min-w-[40px] text-center text-2xl sm:min-w-[50px] sm:text-3xl md:text-[40px] lg:text-[60px]">
                  {plateSerial}
                </span>
                <span className="min-w-[20px] text-center text-2xl sm:min-w-[30px] sm:text-3xl md:text-[40px] lg:mb-3 lg:text-[60px]">
                  {plateLetters}
                </span>
                <span className="min-w-[30px] text-center text-2xl sm:min-w-[40px] sm:text-3xl md:text-[40px] lg:text-[60px]">
                  {plateNumbers}
                </span>
              </div>
            </div>

            <div className="flex w-[60px] flex-col items-center justify-between gap-1 border-l-4 border-black p-1 sm:w-[100px]">
              <div className="flex items-center justify-center">
                <Image
                  src={irImg}
                  alt="شماره پلاک"
                  className="h-full w-full object-contain"
                />
              </div>
              <div
                className={cn(
                  "text-2xl font-bold sm:text-3xl md:text-[40px]",
                  plateTextColor,
                )}
              >
                {plateRegion}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section - Full Width with seamless connection */}
      <div
        ref={contentRef}
        className="-mt-2 overflow-hidden rounded-b-lg border-4 border-t-0 border-black bg-black text-white transition-all duration-300"
        style={{ maxHeight: contentMaxHeight }}
      >
        <div className="relative p-3 pt-4">
          {/* Single continuous vertical divider */}
          <div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 transform bg-gradient-to-b from-gray-100/20 via-white to-gray-100/20"></div>

          {/* First Row */}
          <div className="mb-1 flex items-stretch">
            <div className="flex flex-1 items-center justify-between py-1 pr-4">
              <span className="text-xs font-semibold text-white sm:text-sm md:text-base md:font-bold">
                {plateNum}
              </span>
              <span className="text-xs text-gray-300">شماره پلاک</span>
            </div>
            <div className="flex flex-1 items-center justify-between py-1 pl-4">
              <span className="text-xs text-gray-300">کد ملی</span>
              <span className="text-xs font-semibold text-white sm:text-sm md:text-base md:font-bold">
                {plateInput.national_code}
              </span>
            </div>
          </div>

          {/* Horizontal divider with gradient */}
          <div className="my-0 h-px bg-gradient-to-r from-gray-100/20 via-white to-gray-100/20"></div>

          {/* Second Row */}
          <div className="mt-1 flex items-stretch">
            <div className="flex flex-1 items-center justify-between py-1 pr-4">
              <span className="text-xs font-semibold text-white sm:text-sm md:text-base md:font-bold">
                {plateId}
              </span>
              <span className="text-xs text-gray-300">شناسه</span>
            </div>
            <div className="flex flex-1 items-center justify-between py-1 pl-4">
              {detachDate ? (
                <>
                  <span className="text-xs text-gray-300">تاریخ فک پلاک</span>
                  <span className="text-xs font-semibold text-white sm:text-sm md:text-base md:font-bold">
                    {inactiveDate}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-xs text-gray-300">وضعیت</span>
                  <span className="text-sm font-semibold text-white md:text-base md:font-bold">
                    فعال
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPlateInquiry;
